import { useMemo, type CSSProperties } from "react";
import { Library } from "lucide-react";
import {
  Button,
  EmptyState,
  ModuleLibraryCard,
  Pagination,
  Skeleton,
  enterDelay,
  useCursorPagination,
} from "@playbookai/ui";
import type { KpiDefinition } from "../api/types.js";
import { useKpiDefinitionPage } from "../hooks/useKpiDefinitionPage.js";
import { categoryNodeOf } from "./kpi-library-taxonomy.js";
import { valueTypeLabel } from "./kpi-presentation.js";

export interface KpiLibraryViewProps {
  readonly canManage: boolean;
  readonly onUseDefinition: (definitionId: string) => void;
}

/* The KPI Library as the Direction B canvas draws it (PB-5134, d35): one
 * intro line and a flat grid of template cards — icon tile, name, value type
 * and category, description, and a Create action for administrators. The
 * catalog's collections and categories still order the grid; they are not a
 * navigation of their own here. */

function compareDefinitions(a: KpiDefinition, b: KpiDefinition): number {
  const byCategory = categoryNodeOf(a).name.localeCompare(
    categoryNodeOf(b).name,
    "en",
  );
  return byCategory !== 0 ? byCategory : a.name.localeCompare(b.name, "en");
}

function TemplateCard({
  definition,
  index,
  canManage,
  onUse,
}: {
  definition: KpiDefinition;
  /** Position in the grid, for the capped arrival stagger (ADR-0091). */
  index: number;
  canManage: boolean;
  onUse: () => void;
}): JSX.Element {
  return (
    <li
      className="pb-enter-row flex"
      style={enterDelay(index) as CSSProperties}
    >
      {/* icontile-new-part + module-library-card-open: the shared card
          skeleton for module libraries, static (no onOpen/href — only the
          Create button acts). */}
      <ModuleLibraryCard
        className="w-full"
        title={definition.name}
        description={definition.description || "No description provided."}
        icon={<Library />}
        detailsSlot={
          <p
            className="text-dense-1 text-pb-text-muted"
            title={definition.unit}
          >
            {`${valueTypeLabel(definition.unit)} · ${categoryNodeOf(definition).name}`}
          </p>
        }
        actionsSlot={
          canManage ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={onUse}
              aria-label={`Create a KPI from ${definition.name}`}
            >
              Create
            </Button>
          ) : undefined
        }
      />
    </li>
  );
}

export function KpiLibraryView({
  canManage,
  onUseDefinition,
}: KpiLibraryViewProps): JSX.Element {
  const pageSize = 12;
  const paging = useCursorPagination("kpi-library", { pageSize });
  const query = useKpiDefinitionPage(paging.cursor, pageSize);
  const definitions = useMemo(
    () => [...(query.data?.data ?? [])].sort(compareDefinitions),
    [query.data],
  );

  if (query.data?.totalCount !== undefined) {
    paging.reportTotal(query.data.totalCount);
  }

  return (
    <section aria-labelledby="kpi-library-heading" className="space-y-4">
      <h2 id="kpi-library-heading" className="sr-only">
        KPI Library
      </h2>
      <p className="text-body text-pb-text-secondary">
        Create a KPI from a ready-made template. Templates set the unit and
        direction for you.
      </p>

      {query.isLoading ? (
        <div
          role="status"
          aria-label="Loading KPI Library"
          aria-busy="true"
          className="grid gap-4 sm:grid-cols-2 @min-[58.5rem]/content:grid-cols-3"
        >
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-36" shape="card" />
          ))}
        </div>
      ) : query.isError ? (
        <EmptyState
          variant="error"
          title="KPI Library could not be loaded"
          description="The KPI template catalog is temporarily unavailable. Try the request again."
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}
        />
      ) : definitions.length === 0 ? (
        <EmptyState
          variant="first-use"
          title="KPI Library is empty"
          description="Approved KPI templates have not been provisioned for this workspace yet."
        />
      ) : (
        <ul
          aria-label="KPI templates"
          // Three columns from the page's own width (KpiManager's /content
          // container), not the window: the Player AI side rail narrows the
          // page without changing the window (PB-5436).
          className="grid gap-4 sm:grid-cols-2 @min-[58.5rem]/content:grid-cols-3"
        >
          {definitions.map((definition, index) => (
            <TemplateCard
              key={definition.definitionId}
              definition={definition}
              index={index}
              canManage={canManage}
              onUse={() => onUseDefinition(definition.definitionId)}
            />
          ))}
        </ul>
      )}
      {paging.page > 1 || query.data?.hasMore ? (
        <div className="flex justify-end border-t border-pb-border/50 pt-3">
          <Pagination
            mode="cursor"
            page={paging.page}
            pageItemCount={definitions.length}
            totalCount={paging.totalCount}
            canGoBack={paging.canGoBack}
            hasNext={Boolean(query.data?.hasMore && query.data.nextCursor)}
            busy={query.isFetching}
            onPrev={paging.goBack}
            onNext={() => {
              const cursor = query.data?.nextCursor;
              if (cursor) paging.goNext(cursor);
            }}
          />
        </div>
      ) : null}
    </section>
  );
}
